/**
 * Espelho client-side das regras de adesão (6 blocos / 10 requisitos), para o
 * PREVIEW do painel de conformidade durante a entrevista. Fonte: carona-inteligente
 * /rules/compliance_rules.yaml. A avaliação AUTORITATIVA é do backend (riscos +
 * documento_estruturado.checklist em POST /v1/justificativas).
 */
import {AtaDetalhe, RespostasEntrevista} from './types'

export type ReqStatus = 'ok' | 'risco' | 'pendente' | 'lembrete' | 'na'
export type Severidade = 'BAIXA' | 'MEDIA' | 'ALTA' | 'BLOQUEANTE'

export interface RequisitoAval {
    id: string
    bloco: number
    titulo: string
    base_legal: string
    tipo: 'obrigatorio' | 'condicional' | 'lembrete'
    severidade: Severidade
    status: ReqStatus
    hint: string
}

export const BLOCOS: {numero: number; titulo: string; descricao: string}[] = [
    {numero: 1, titulo: 'Identificação e necessidade', descricao: 'Motivação compatível com a demanda real (TCU 2630/2024).'},
    {numero: 2, titulo: 'Vantajosidade', descricao: 'Pesquisa de preços própria (TCU 420/2018 e 1794/2023).'},
    {numero: 3, titulo: 'Limites quantitativos', descricao: '50% por item (§3º) e 200% coletivo (§4º).'},
    {numero: 4, titulo: 'Vigência e esfera', descricao: 'Ata vigente (art. 84) e vedação de esfera (art. 86, §8º).'},
    {numero: 5, titulo: 'Anuências', descricao: 'Gerenciador e fornecedor (art. 86, §2º).'},
    {numero: 6, titulo: 'Edital de origem', descricao: 'Cláusula de adesão por não participantes (art. 86).'},
]

const ESFERAS_SUBNACIONAIS = ['E', 'M', 'D']

/** evidencias: mapa requisito_id -> referência/descrição preenchida pelo usuário. */
export function avaliarConformidade(
    ata: AtaDetalhe,
    r: RespostasEntrevista,
    evidencias: Record<string, string>
): RequisitoAval[] {
    const temEvidencia = (id: string) => Boolean((evidencias[id] || '').trim())
    const out: RequisitoAval[] = []

    // Bloco 1
    out.push({
        id: 'motivacao_necessidade', bloco: 1, tipo: 'obrigatorio', severidade: 'ALTA',
        titulo: 'Motivação da necessidade', base_legal: 'Art. 86; TCU 2630/2024',
        status: r.demanda_descricao.trim().length > 10 ? 'ok' : 'pendente',
        hint: r.demanda_descricao.trim().length > 10 ? 'Demanda descrita.' : 'Descreva a demanda concreta.',
    })
    if (!r.demanda_prevista_pca) {
        out.push({
            id: 'previsao_pca', bloco: 1, tipo: 'condicional', severidade: 'MEDIA',
            titulo: 'Demanda prevista no PCA', base_legal: 'Art. 12, VII; art. 18',
            status: 'pendente', hint: 'Justifique a ausência ou referencie o planejamento.',
        })
    }
    if (r.alega_urgencia) {
        out.push({
            id: 'urgencia_desabastecimento', bloco: 1, tipo: 'condicional', severidade: 'ALTA',
            titulo: 'Urgência comprovada', base_legal: 'Art. 86 (motivação)',
            status: temEvidencia('urgencia_desabastecimento') ? 'ok' : 'pendente',
            hint: temEvidencia('urgencia_desabastecimento') ? 'Evidência anexada.' : 'Anexe a evidência do risco (não basta alegar).',
        })
    }

    // Bloco 2 — vantajosidade (v1: declaração vira alerta)
    out.push({
        id: 'vantajosidade', bloco: 2, tipo: 'obrigatorio', severidade: 'BLOQUEANTE',
        titulo: 'Vantajosidade comprovada', base_legal: 'Art. 86; TCU 420/2018 e 1794/2023',
        status: r.declara_preco_compativel ? 'ok' : 'pendente',
        hint: r.declara_preco_compativel
            ? 'Declarado compatível — a pesquisa de preços própria reforça na geração.'
            : 'Sem pesquisa de preços própria, não atinge APTA.',
    })

    // Bloco 3 — limites
    const itens = ata.itens || []
    const algumaQtd = itens.some((it) => (r.quantidades_desejadas[String(it.numero_item)] || 0) > 0)
    const excedeIndividual = itens.some(
        (it) => (r.quantidades_desejadas[String(it.numero_item)] || 0) > it.limite_50pct
    )
    out.push({
        id: 'limite_individual_50', bloco: 3, tipo: 'obrigatorio', severidade: 'BLOQUEANTE',
        titulo: 'Limite individual de 50%', base_legal: 'Art. 86, §3º',
        status: !algumaQtd ? 'pendente' : excedeIndividual ? 'pendente' : 'ok',
        hint: !algumaQtd ? 'Informe as quantidades por item.'
            : excedeIndividual ? 'Quantidade excede 50% do registrado.' : 'Dentro do teto de 50%.',
    })
    const saldoOk = ata.saldo_status === 'DISPONIVEL'
    const coletivoOk = saldoOk || r.ciente_anuencias
    out.push({
        id: 'limite_coletivo_200', bloco: 3, tipo: 'obrigatorio', severidade: 'ALTA',
        titulo: 'Limite coletivo de 200%', base_legal: 'Art. 86, §4º',
        status: coletivoOk ? 'ok' : 'risco',
        hint: saldoOk ? 'Saldo disponível.'
            : r.ciente_anuencias ? 'A confirmar o saldo coletivo com o gerenciador.'
            : 'Saldo coletivo a confirmar com o gerenciador.',
    })

    // Bloco 4 — vigência e esfera (automáticos)
    const vigente = ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia)
    out.push({
        id: 'vigencia_ata', bloco: 4, tipo: 'obrigatorio', severidade: 'BLOQUEANTE',
        titulo: 'Ata vigente', base_legal: 'Art. 84',
        status: vigente ? 'ok' : 'pendente',
        hint: vigente ? 'Ata vigente.' : 'Ata fora de vigência.',
    })
    const vedado = r.esfera_aderente === 'F' && ESFERAS_SUBNACIONAIS.includes(ata.esfera_codigo)
    out.push({
        id: 'vedacao_esfera', bloco: 4, tipo: 'obrigatorio', severidade: 'BLOQUEANTE',
        titulo: 'Compatibilidade de esfera', base_legal: 'Art. 86, §8º',
        status: vedado ? 'pendente' : 'ok',
        hint: vedado ? 'Federal não adere a ata subnacional.' : 'Esferas compatíveis.',
    })

    // Bloco 5 — anuências
    out.push({
        id: 'anuencia_gerenciador', bloco: 5, tipo: 'obrigatorio', severidade: 'ALTA',
        titulo: 'Anuência do gerenciador', base_legal: 'Art. 86, §2º',
        status: temEvidencia('anuencia_gerenciador') || r.ciente_anuencias ? 'ok' : 'pendente',
        hint: temEvidencia('anuencia_gerenciador') ? 'Autorização registrada.'
            : r.ciente_anuencias ? 'Ciente — obtenha a autorização do gerenciador antes de formalizar.'
            : 'Anexe a autorização do gerenciador.',
    })
    out.push({
        id: 'aceite_fornecedor', bloco: 5, tipo: 'obrigatorio', severidade: 'ALTA',
        titulo: 'Aceite do fornecedor', base_legal: 'Art. 86, §2º',
        status: temEvidencia('aceite_fornecedor') || r.ciente_anuencias ? 'ok' : 'pendente',
        hint: temEvidencia('aceite_fornecedor') ? 'Aceite registrado.'
            : r.ciente_anuencias ? 'Ciente — obtenha o aceite do fornecedor antes de formalizar.'
            : 'Anexe o aceite do fornecedor.',
    })

    // Bloco 6 — edital (a confirmar no edital de origem; o gestor confirma com o Analisador de Editais)
    out.push({
        id: 'edital_preve_adesao', bloco: 6, tipo: 'obrigatorio', severidade: 'ALTA',
        titulo: 'Edital prevê adesão', base_legal: 'Art. 86; Dec. 11.462/2023',
        status: r.ciente_edital ? 'ok' : 'risco',
        hint: r.ciente_edital ? 'Ciente — a confirmar no edital de origem.' : 'Confirme a cláusula de adesão no edital de origem.',
    })
    out.push({
        id: 'controle_previo_publicidade', bloco: 6, tipo: 'lembrete', severidade: 'MEDIA',
        titulo: 'Controle prévio e publicidade', base_legal: 'Art. 53; art. 94',
        status: 'lembrete', hint: 'Parecer jurídico + publicidade no PNCP são etapas do órgão.',
    })

    return out
}

export function resumoConformidade(reqs: RequisitoAval[]): {ok: number; total: number; pct: number} {
    const obrig = reqs.filter((x) => x.tipo === 'obrigatorio')
    const ok = obrig.filter((x) => x.status === 'ok').length
    const total = obrig.length || 1
    return {ok, total, pct: Math.round((ok / total) * 100)}
}
